import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Remove, Add } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { ButtonPrimary } from '../../ui/ButtonPrimary';
import { CardSurface } from '../../ui/CardSurface';

// Valores permitidos
const VALORES_PERMITIDOS = [-100, -80, -60, -40, -20, 0, 20, 40, 60, 80, 100];

export function AjusteValorModal({ valorInicial = 0, onConfirm, onCancel }) {
  const valorInicialInteiro = Math.round(valorInicial);
  const valorInicialValido = VALORES_PERMITIDOS.includes(valorInicialInteiro)
    ? valorInicialInteiro
    : 0;

  const [valor, setValor] = useState(valorInicialValido);
  const [valorInvalido, setValorInvalido] = useState(false);

  useEffect(() => {
    const valorInteiro = Math.round(valor);
    setValorInvalido(!VALORES_PERMITIDOS.includes(valorInteiro));
  }, [valor]);

  const ajustar = (delta) => {
    setValor((prev) => {
      const novoValor = Math.round(prev) + delta;
      const valorLimitado = Math.max(-100, Math.min(100, novoValor));
      return valorLimitado;
    });
  };

  const handleConfirm = () => {
    const valorInteiro = Math.round(valor);
    if (!VALORES_PERMITIDOS.includes(valorInteiro)) {
      return;
    }
    onConfirm(valorInteiro);
  };

  const valorInteiro = Math.round(valor);
  const podeConfirmar = VALORES_PERMITIDOS.includes(valorInteiro);

  return (
    <Dialog
      open={true}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '14px',
        },
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1, fontWeight: 600, pt: 3 }}>
          Ajustar Valor
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 2,
            }}
          >
            <motion.div
              key={valorInteiro}
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              <CardSurface
                sx={{
                  mb: 4,
                  px: 4,
                  py: 3,
                  bgcolor: valorInvalido ? 'error.light' : 'primary.light',
                  border: `2px solid ${valorInvalido ? 'error.main' : 'primary.main'}`,
                  minWidth: 200,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h2"
                  component="span"
                  fontWeight="bold"
                  color={valorInvalido ? 'error.dark' : 'primary.dark'}
                  sx={{ fontSize: { xs: '3rem', sm: '4rem' } }}
                >
                  {valorInteiro >= 0 ? '+' : ''}{valorInteiro}
                </Typography>
                <Typography variant="h6" component="span" color="text.secondary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, ml: 1 }}>
                  ml
                </Typography>
              </CardSurface>
            </motion.div>

            {valorInvalido && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{ width: '100%' }}
              >
                <Alert severity="warning" sx={{ mb: 3, width: '100%', borderRadius: '12px' }}>
                  Valor inválido. Ajuste o valor usando os botões abaixo.
                </Alert>
              </motion.div>
            )}

            <Box
              sx={{
                display: 'flex',
                gap: 3,
                width: '100%',
                justifyContent: 'center',
                mt: 2,
              }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <ButtonPrimary
                  color="error"
                  size="large"
                  onClick={() => ajustar(-20)}
                  disabled={valorInteiro <= -100}
                  startIcon={<Remove />}
                  sx={{
                    minWidth: 140,
                    py: 2,
                    fontSize: '1.25rem',
                    fontWeight: 700,
                  }}
                >
                  20
                </ButtonPrimary>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <ButtonPrimary
                  color="success"
                  size="large"
                  onClick={() => ajustar(20)}
                  disabled={valorInteiro >= 100}
                  startIcon={<Add />}
                  sx={{
                    minWidth: 140,
                    py: 2,
                    fontSize: '1.25rem',
                    fontWeight: 700,
                  }}
                >
                  20
                </ButtonPrimary>
              </motion.div>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <ButtonPrimary
            onClick={onCancel}
            variant="outlined"
            size="large"
            sx={{ 
              minWidth: 120,
              borderColor: 'grey.300',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'grey.400',
                bgcolor: 'grey.50',
              },
            }}
          >
            Cancelar
          </ButtonPrimary>
          <ButtonPrimary
            onClick={handleConfirm}
            size="large"
            disabled={!podeConfirmar}
            sx={{ minWidth: 120 }}
          >
            Confirmar
          </ButtonPrimary>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
}
